import { PROD_ENV, STAGING_ENV } from "../../utils/constants/appConstants";
import MixpanelEventType from "./MixpanelEventType";
import {
  trackEvent,
  identifyUser,
  setUserProperties,
  setOnceUserProperties,
  userLoginCountProperties,
  reset
} from "./mixpanel";

/**
 * Custom hook to handle Mixpanel event actions.
 *
 * @returns {Object} An object containing functions to track various Mixpanel events.
 * @property {Function} signupMixpanelEvent - Tracks the signup event.
 * @property {Function} loginMixpanelEvent - Tracks the login event.
 * @property {Function} logoutMixpanelEvent - Tracks the logout event.
 * @property {Function} xeroConnectMixpanelEvent - Tracks the Xero connect event.
 * @property {Function} myobConnectMixpanelEvent - Tracks the MYOB connect event.
 * @property {Function} quickbookConnectMixpanelEvent - Tracks the QuickBooks connect event.
 * @property {Function} reckonConnectMixpanelEvent - Tracks the Reckon connect event.
 * @property {Function} preApprovedClientStatusMixpanelEvent - Tracks the pre-approved client status event.
 * @property {Function} approvedClientStatusMixpanelEvent - Tracks the approved client status event.
 */
const useMixpanelEventAction = () => {
  const { MIXPANEL_EVENTS } = MixpanelEventType();

  const checkNotNullField = (fieldValue) => {
    return fieldValue !== null && fieldValue !== "";
  };

  const checkEnvironment = () => {
    const environment = process.env.REACT_APP_ENV;
    return environment === PROD_ENV || environment === STAGING_ENV;
  };

  const updateUserDetailsProperties = (properties = {}) => {
    const updateUserDetails = MIXPANEL_EVENTS.UPDATE_USER_DETAILS;

    updateUserDetails.label.$first_name = properties?.user?.firstName;
    updateUserDetails.label.$last_name = properties?.user?.lastName;
    updateUserDetails.label.$phone = properties?.user?.primaryContactNumber;
    updateUserDetails.label.$email = properties?.user?.primaryEmail;
    updateUserDetails.label["Display Name"] = properties?.user?.displayName;
    const roleList = properties?.user?.roles.map((role) => role.name);
    updateUserDetails.label["Role"] = roleList;
    updateUserDetails.label["Last Visit"] = properties?.lastLoginAt;

    const middleName = properties?.user?.middleName;
    if (checkNotNullField(middleName)) {
      updateUserDetails.label["Middle Name"] = middleName;
    } else {
      delete updateUserDetails.label["Middle Name"];
    }

    const secondaryContactNumber = properties?.user?.secondaryContactNumber;
    if (checkNotNullField(secondaryContactNumber)) {
      updateUserDetails.label["Phone (Secondary)"] = secondaryContactNumber;
    } else {
      delete updateUserDetails.label["Phone (Secondary)"];
    }

    const dateOfBirth = properties?.user?.userAccountMeta?.dateOfBirth;
    if (checkNotNullField(dateOfBirth)) {
      updateUserDetails.label["Date of Birth"] = dateOfBirth;
    } else {
      delete updateUserDetails.label["Date of Birth"];
    }

    const referralCode = properties?.user?.userAccountMeta?.referralCode;
    if (checkNotNullField(referralCode)) {
      updateUserDetails.label["Referrer"] = referralCode;
    } else {
      delete updateUserDetails.label["Referrer"];
    }

    const promoCode = null;
    if (checkNotNullField(promoCode)) {
      updateUserDetails.label["Promo Code"] = promoCode;
    } else {
      delete updateUserDetails.label["Promo Code"];
    }

    identifyUser(properties?.user?.id);
    setUserProperties(updateUserDetails.label);
    setOnceUserProperties({ "First Visit": new Date().toISOString() });
  };

  const signupMixpanelEvent = (properties = {}) => {
    if (!checkEnvironment()) return;
    const organicSignupEvents = MIXPANEL_EVENTS.ORGANIC_SIGNUP_EVENT;
    const referralSignupEvents = MIXPANEL_EVENTS.REFERRAL_SIGNUP_EVENT;
    const demoSignupEvents = MIXPANEL_EVENTS.SIGNUP_COMPLETE_DEMO_ACCOUNT;
    const testDemoPattern = /test|demo/i;
    const testEmail = /@mailinator\.com$/i;

    if (properties?.user?.userAccountMeta?.referralCode !== null) {
      trackEvent(referralSignupEvents.type, referralSignupEvents.label);
    } else if (
      testEmail.test(properties?.user?.primaryEmail) ||
      testDemoPattern.test(properties?.user?.firstName) ||
      testDemoPattern.test(properties?.user?.lastName)
    ) {
      trackEvent(demoSignupEvents.type, demoSignupEvents.label);
    } else {
      trackEvent(organicSignupEvents.type, organicSignupEvents.label);
    }
    updateUserDetailsProperties(properties);
  };

  const completeDemoAccountMixpanelEvent = (email) => {
    if (!checkEnvironment()) return;
    const completeDemoAccountEvents = MIXPANEL_EVENTS.COMPLETE_DEMO_ACCOUNT;
    completeDemoAccountEvents.label.email = email;
    trackEvent(completeDemoAccountEvents.type, completeDemoAccountEvents.label);
  };

  const loginMixpanelEvent = (properties = {}) => {
    if (!checkEnvironment()) return;
    const loginEvents = MIXPANEL_EVENTS.LOGIN_EVENT;
    trackEvent(loginEvents.type, loginEvents.label);
    updateUserDetailsProperties(properties);
    userLoginCountProperties({ "Login Count": 1 });
  };

  const logoutMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const logoutEvents = MIXPANEL_EVENTS.LOGOUT_EVENT;
    trackEvent(logoutEvents.type, logoutEvents.label);
    reset();
  };

  const emailConfirmationMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const confirmationEvents = MIXPANEL_EVENTS.CONFIRMATION_EVENT;
    trackEvent(confirmationEvents.type, confirmationEvents.label);
  };

  const xeroConnectMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.XERO_CONNECT_ACCOUNTING_SOFTWARE;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const myobConnectMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.MYOB_CONNECT_ACCOUNTING_SOFTWARE;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const quickbookConnectMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.QUICKBOOK_CONNECT_ACCOUNTING_SOFTWARE;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const reckonConnectMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.RECKON_CONNECT_ACCOUNTING_SOFTWARE;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const preApprovedClientStatusMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.CLIENT_STATUS_PRE_APPROVED;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const approvedClientStatusMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const connectEvents = MIXPANEL_EVENTS.CLIENT_STATUS_APPROVED;
    trackEvent(connectEvents.type, connectEvents.label);
  };

  const approvedInvoiceMixpanelEvent = (properties = []) => {
    if (!checkEnvironment()) return;
    const approvedInvoiceEvents = MIXPANEL_EVENTS.APPROVE_INVOICE;
    properties.forEach((invoice) => {
      approvedInvoiceEvents.label["FT Customer ID"] = invoice?.userAccountId;
      approvedInvoiceEvents.label["FT Quote ID"] = invoice?.quoteId;
      approvedInvoiceEvents.label.current_status = invoice?.currentStatus;
      trackEvent(approvedInvoiceEvents.type, approvedInvoiceEvents.label);
    });
  };

  const fundedInvoiceMixpanelEvent = (properties = []) => {
    if (!checkEnvironment()) return;
    const fundedInvoiceEvents = MIXPANEL_EVENTS.FUNDED_INVOICE;
    properties.forEach((invoice) => {
      fundedInvoiceEvents.label["FT Customer ID"] = invoice?.userAccountId;
      fundedInvoiceEvents.label["FT Quote ID"] = invoice?.quoteId;
      trackEvent(fundedInvoiceEvents.type, fundedInvoiceEvents.label);
    });
  };

  const rejectInvoiceMixpanelEvent = (properties = []) => {
    if (!checkEnvironment()) return;
    const rejectInvoiceEvents = MIXPANEL_EVENTS.REJECT_INVOICE;
    properties.forEach((invoice) => {
      rejectInvoiceEvents.label["FT Customer ID"] = invoice?.userAccountId;
      rejectInvoiceEvents.label["FT Quote ID"] = invoice?.quoteId;
      trackEvent(rejectInvoiceEvents.type, rejectInvoiceEvents.label);
    });
  };

  const submitInvoiceMixpanelEvent = (properties = {}) => {
    if (!checkEnvironment()) return;
    const submitInvoiceEvents = MIXPANEL_EVENTS.SUBMIT_INVOICE;
    submitInvoiceEvents.label.facility_limit = properties?.facilityLimit;
    submitInvoiceEvents.label.invoice_bucket = properties?.invoiceBucket;
    submitInvoiceEvents.label.user_id = properties?.userAccountId;
    trackEvent(submitInvoiceEvents.type, submitInvoiceEvents.label);
  };

  const manualInvoiceUploadMixpanelEvent = (properties = {}) => {
    if (!checkEnvironment()) return;
    const manualInvoiceUploadEvents = MIXPANEL_EVENTS.MANUAL_INVOICE_UPLOAD;
    manualInvoiceUploadEvents.label.user_id = properties?.userAccountId;
    manualInvoiceUploadEvents.label.invoices_count = 1;
    manualInvoiceUploadEvents.label.is_first_upload =
      properties?.isFirstInvoiceUpload;
    trackEvent(manualInvoiceUploadEvents.type, manualInvoiceUploadEvents.label);
  };

  const manualInvoiceFileUploadMixpanelEvent = (properties = []) => {
    if (!checkEnvironment()) return;
    const manualInvoiceFileUploadEvent = MIXPANEL_EVENTS.MANUAL_INVOICE_UPLOAD;
    manualInvoiceFileUploadEvent.label.user_id = properties?.userAccountId;
    manualInvoiceFileUploadEvent.label.invoices_count =
      properties?.validInvoicesCount;
    manualInvoiceFileUploadEvent.label.is_first_upload =
      properties?.isFirstInvoiceUpload;
    trackEvent(
      manualInvoiceFileUploadEvent.type,
      manualInvoiceFileUploadEvent.label
    );
  };

  const completeAMLMixpanelEvent = () => {
    if (!checkEnvironment()) return;
    const completeAMLEvents = MIXPANEL_EVENTS.COMPLETE_AML;
    trackEvent(completeAMLEvents.type, completeAMLEvents.label);
  };

  const completeAplyIdMixpanelEvent = (userAccountId) => {
    if (!checkEnvironment()) return;
    const completeAplyIdEvents = MIXPANEL_EVENTS.APPLYID_COMPLETE;
    completeAplyIdEvents.label["FT Customer ID"] = userAccountId;
    trackEvent(completeAplyIdEvents.type, completeAplyIdEvents.label);
  };

  return {
    signupMixpanelEvent,
    completeDemoAccountMixpanelEvent,
    loginMixpanelEvent,
    logoutMixpanelEvent,
    emailConfirmationMixpanelEvent,
    xeroConnectMixpanelEvent,
    myobConnectMixpanelEvent,
    quickbookConnectMixpanelEvent,
    reckonConnectMixpanelEvent,
    preApprovedClientStatusMixpanelEvent,
    approvedClientStatusMixpanelEvent,
    approvedInvoiceMixpanelEvent,
    fundedInvoiceMixpanelEvent,
    rejectInvoiceMixpanelEvent,
    submitInvoiceMixpanelEvent,
    manualInvoiceUploadMixpanelEvent,
    manualInvoiceFileUploadMixpanelEvent,
    completeAMLMixpanelEvent,
    completeAplyIdMixpanelEvent
  };
};

export default useMixpanelEventAction;
